import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Token storage keys
const TOKEN_KEY = 'outlet_auth_token';
const USER_KEY = 'outlet_user';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  // Configure axios interceptor for auth headers
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const savedToken = localStorage.getItem(TOKEN_KEY);
        if (savedToken) {
          config.headers.Authorization = `Bearer ${savedToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Only logout if user was previously authenticated (has saved token)
          const savedToken = localStorage.getItem(TOKEN_KEY);
          if (savedToken) {
            logout();
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const checkAuth = async () => {
    try {
      // Check for saved token
      const savedToken = localStorage.getItem(TOKEN_KEY);
      const savedUser = localStorage.getItem(USER_KEY);
      
      if (savedToken && savedUser) {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
        
        // Verify token is still valid
        try {
          const response = await axios.get(`${API}/auth/me/v2`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          localStorage.setItem(USER_KEY, JSON.stringify(response.data));
        } catch (e) {
          // Token invalid, clear storage
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setUser(null);
          setToken(null);
        }
      } else {
        // Try cookie-based auth (for admin users)
        // Only check if there's a session_token cookie present
        const hasCookie = document.cookie.includes('session_token');
        if (hasCookie) {
          try {
            const response = await axios.get(`${API}/auth/me`, {
              withCredentials: true
            });
            setUser(response.data);
          } catch (e) {
            setUser(null);
          }
        } else {
          setUser(null);
        }
      }
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData, accessToken) => {
    // Save to state
    setUser(userData);
    setToken(accessToken);
    
    // Persist to localStorage
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
  };

  const loginRedirect = () => {
    // Redirect to auth page
    window.location.href = '/entrar';
  };

  const logout = async () => {
    try {
      // Try cookie-based logout
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (e) {
      // Silent fail
    }
    
    // Clear local storage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    
    setUser(null);
    setToken(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
  };

  const isProfileComplete = () => {
    if (!user) return false;
    return user.cpf && user.phone;
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      loginRedirect,
      logout,
      updateUser,
      isProfileComplete,
      isAdmin: user?.is_admin || false,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
